import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';

const HabilidadesContainer = styled.div`
  width: 100%;
  min-height: 100vh;
  background: var(--color-dark);
  padding: 2rem;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const Title = styled(motion.h1)`
  color: var(--color-white);
  font-size: 3rem;
  margin-bottom: 3rem;
  text-align: center;
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    bottom: -15px;
    left: 50%;
    transform: translateX(-50%);
    width: 100px;
    height: 3px;
    background-color: var(--color-primary);
    border-radius: 3px;
  }
`;

const SkillsGrid = styled(motion.div)`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 2rem;
  width: 100%;
  max-width: 1200px;
  margin-bottom: 3rem;
`;

const SkillCard = styled(motion.div)`
  background: rgba(26, 26, 26, 0.7);
  border-radius: 15px;
  padding: 2rem;
  box-shadow: 0 0 20px rgba(0, 0, 0, 0.3);
  border: 1px solid rgba(255, 144, 82, 0.2);
  backdrop-filter: blur(10px);
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 5px;
    background: var(--color-primary);
    box-shadow: 0 0 10px rgba(255, 144, 82, 0.5);
  }
`;

const SkillIcon = styled.div`
  font-size: 3rem;
  color: var(--color-primary);
  margin-bottom: 1.5rem;
  filter: drop-shadow(0 0 10px rgba(255, 144, 82, 0.3));
`;

const SkillTitle = styled.h3`
  color: var(--color-white);
  font-size: 1.5rem;
  margin-bottom: 1rem;
  text-align: center;
`;

const SkillDescription = styled.p`
  color: var(--color-white);
  opacity: 0.7;
  text-align: center;
  line-height: 1.6;
  margin-bottom: 1.5rem;
`;

const SkillLevel = styled.div`
  width: 100%;
  height: 10px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 5px;
  margin-top: auto;
  position: relative;
  overflow: hidden;
`;

const SkillLevelFill = styled(motion.div)<{ level: number }>`
  height: 100%;
  width: ${props => props.level}%;
  background: var(--color-primary);
  border-radius: 5px;
  box-shadow: 0 0 10px rgba(255, 144, 82, 0.3);
`;

const SkillTags = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 0.5rem;
  margin: 1rem 0;
`;

const SkillTag = styled.span`
  background: rgba(255, 144, 82, 0.2);
  color: var(--color-white);
  padding: 0.3rem 0.8rem;
  border-radius: 50px;
  font-size: 0.8rem;
  border: 1px solid rgba(255, 144, 82, 0.3);
`;

const SkillCategories = styled.div`
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  gap: 1rem;
  margin-bottom: 2rem;
  width: 100%;
  max-width: 800px;
`;

const CategoryButton = styled(motion.button)<{ active: boolean }>`
  background: ${props => props.active ? 'rgba(255, 144, 82, 0.3)' : 'rgba(26, 26, 26, 0.7)'};
  color: var(--color-white);
  padding: 0.5rem 1.2rem;
  border-radius: 50px;
  border: 1px solid ${props => props.active ? 'var(--color-primary)' : 'rgba(255, 144, 82, 0.2)'};
  cursor: pointer;
  font-size: 1rem;
  box-shadow: ${props => props.active ? '0 0 15px rgba(255, 144, 82, 0.3)' : 'none'};
`;

const BackButton = styled(motion.button)`
  background: rgba(26, 26, 26, 0.7);
  color: var(--color-white);
  font-size: 1rem;
  padding: 0.8rem 1.5rem;
  border-radius: 50px;
  border: 2px solid var(--color-primary);
  margin-top: 2rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 0 15px rgba(255, 144, 82, 0.2);
  backdrop-filter: blur(5px);
  
  &:hover {
    background: rgba(255, 144, 82, 0.2);
  }
`;

const Habilidades: React.FC<{ onBack: () => void }> = ({ onBack }) => {
  const [activeCategory, setActiveCategory] = React.useState('all');
  
 
const categories = [
  { id: 'all', label: 'All' },
  { id: 'programming', label: 'Programming' },
  { id: 'gamedev', label: 'Game Development' },
  { id: 'architecture', label: 'Architecture' },
  { id: 'management', label: 'Management' }
];

const skills = [
  {
    title: "JavaScript/TypeScript",
    icon: "⚛️",
    description: "Extensive frontend and backend development experience with JavaScript, TypeScript, React, Vue, and Node.js.",
    level: 95,
    category: "programming",
    tags: ["React", "Vue", "Node.js", "TypeScript"]
  },
  {
    title: "Unity",
    icon: "🎮",
    description: "Advanced development of interactive applications and games using Unity and C#.",
    level: 90,
    category: "gamedev",
    tags: ["C#", "3D", "2D", "Physics"]
  },
  {
    title: "Unreal Engine",
    icon: "🎯",
    description: "Creating immersive games and experiences with Unreal Engine and C++.",
    level: 85,
    category: "gamedev",
    tags: ["C++", "Blueprints", "Shaders"]
  },
  {
    title: "C# / .NET",
    icon: "🔷",
    description: "Developing robust applications and services using C# and the .NET ecosystem.",
    level: 90,
    category: "programming",
    tags: [".NET Core", "ASP.NET", "Entity Framework"]
  },
  {
    title: "Software Architecture",
    icon: "🏗️",
    description: "Designing scalable, maintainable, and high-performance software architectures.",
    level: 95,
    category: "architecture",
    tags: ["Microservices", "DDD", "SOLID"]
  },
  {
    title: "Technical Leadership",
    icon: "👨‍💻",
    description: "Leading development teams, mentoring engineers, and managing technical projects.",
    level: 90,
    category: "management",
    tags: ["Mentorship", "Project Management", "Communication"]
  },
  {
    title: "Redis",
    icon: "⚡",
    description: "Expert in caching, messaging queues, and high-performance data storage using Redis.",
    level: 85,
    category: "programming",
    tags: ["Cache", "Pub/Sub", "Performance"]
  },
  {
    title: "Critical Thinking",
    icon: "🧠",
    description: "Analyzing complex problems and making informed decisions based on data-driven insights.",
    level: 95,
    category: "management",
    tags: ["Analysis", "Decision Making", "Innovation"]
  }
];

  
  const filteredSkills = activeCategory === 'all' 
    ? skills 
    : skills.filter(skill => skill.category === activeCategory);
  
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { 
      opacity: 1,
      transition: { 
        staggerChildren: 0.1
      }
    }
  };
  
  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { 
        type: "spring", 
        stiffness: 100, 
        damping: 12
      }
    }
  };

  return (
    <HabilidadesContainer>
      <Title
        initial={{ opacity: 0, y: -50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
      >
        My Skills
      </Title>
      
      <SkillCategories>
        {categories.map(category => (
          <CategoryButton
            key={category.id}
            active={activeCategory === category.id}
            onClick={() => setActiveCategory(category.id)}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            {category.label}
          </CategoryButton>
        ))}
      </SkillCategories>
      
      <SkillsGrid
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        {filteredSkills.map((skill, index) => (
          <SkillCard
            key={index}
            variants={itemVariants}
          >
            <SkillIcon>{skill.icon}</SkillIcon>
            <SkillTitle>{skill.title}</SkillTitle>
            <SkillTags>
              {skill.tags.map((tag, i) => (
                <SkillTag key={i}>{tag}</SkillTag>
              ))}
            </SkillTags>
            <SkillDescription>{skill.description}</SkillDescription>
            <SkillLevel>
              <SkillLevelFill 
                level={skill.level}
                initial={{ width: 0 }}
                animate={{ width: `${skill.level}%` }}
                transition={{ duration: 1, delay: index * 0.1 }}
              />
            </SkillLevel>
          </SkillCard>
        ))}
      </SkillsGrid>
      
      <BackButton 
        onClick={onBack}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
      >
        Back to Menu
      </BackButton>
    </HabilidadesContainer>
  );
};

export default Habilidades;
